import { Pipe, PipeTransform } from '@angular/core';

import { getRewardDisplayExpirationDate } from './reward-display-expiration.utils';

/**
 * Applies the "last full day" business rule and returns the result as an ISO
 * timestamp (e.g. `2026-09-14T23:59:59.000Z`), WITHOUT formatting it for
 * display.
 *
 * This is the single source of truth for the reward expiration date, shared by
 * My Account and Cart so both surfaces show the same date.
 *
 * - For the HMF `hmf-wc-scorecard-reward-tile` `[expirationDate]` input, use the
 *   pipe output directly: the tile parses the value with `new Date(...)` for its
 *   internal expiry/countdown logic, so it needs the exact rolled-back moment
 *   (the 23:59:59 end-of-day boundary).
 * - For visible date text, chain Angular's date pipe to format the ISO, e.g.
 *   `| rewardDisplayExpiration: localDay() | date: 'MM/dd/yyyy'`.
 *
 * The pipe is PURE. Its output for a given expiration string only changes when
 * the local calendar day changes, so it is driven by an explicit `localDay`
 * token (the second argument) — typically `LocalDayService.localDay()`, which
 * updates once per local midnight. This keeps the pipe cacheable (no work on
 * every change-detection pass) while still rolling the date over at local
 * midnight. When `localDay` is omitted, the current time is used.
 *
 * Legacy `MM/DD/YYYY` values (the format the old `ExpirationDatePipe` accepted
 * and displayed as-is) are parsed as the end of that local day, so the
 * last-full-day rule keeps the date unchanged and the reward still displays —
 * matching the old pipe's behavior for that format.
 *
 * For nullish, whitespace-only, or unparseable input the pipe returns `''`
 * (never the raw string). `''` is safe for both usages: the chained `| date`
 * pipe renders nothing instead of throwing on an unparseable value, and the
 * HMF tile already treats `''` as "no expiration".
 */
@Pipe({
  name: 'rewardDisplayExpiration',
  standalone: true,
})
export class RewardDisplayExpirationPipe implements PipeTransform {
  transform(value: string | null | undefined, localDayMs?: number): string {
    if (!value?.trim()) {
      return '';
    }

    const now = new Date(localDayMs ?? Date.now());
    const displayExpirationDate = getRewardDisplayExpirationDate(value, now);

    // Return the rolled-back ISO so the tile keeps the 23:59:59 boundary.
    // When the value can't be parsed, return '' — a DatePipe-safe value (the
    // chained `| date` pipe renders nothing instead of throwing on an
    // unparseable string) and one the HMF tile already treats as "no
    // expiration" (it receives '' for empty inputs today).
    return displayExpirationDate ? displayExpirationDate.toISOString() : '';
  }
}
